import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { amendOrder } from '../api'
import type { ShadowOrder } from '../types'

/**
 * Emir defterinden hızlı düzenleme balonu — modal değil, tıklanan hücrenin
 * yanında açılır ve dışarı tıklayınca **vazgeçilmiş** sayılır.
 *
 * `document.body`ye portal ile basılıyor: `.ladder-cell` ve `.ladder`
 * `overflow: hidden` taşıyor, balon defterin içinde kalsaydı kırpılırdı.
 * Konum çapa hücrenin ekran koordinatlarından hesaplanıyor ve ekran dışına
 * taşmayacak biçimde kenetleniyor.
 *
 * Kaydetme `PATCH /api/orders/{id}` — kimlik korunur, sunucuda yeni sürüm
 * oluşur, eski hâl kalıcı kayıtta durur.
 */

interface Props {
  order: ShadowOrder
  /** Çapa hücrenin açılış anındaki ekran dikdörtgeni. */
  anchor: DOMRect
  onClose: () => void
}

const GENISLIK = 208

export function OrderEditPopover({ order, anchor, onClose }: Props) {
  const [price, setPrice] = useState(String(order.price))
  const [quantity, setQuantity] = useState(String(order.quantity))
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const kutuRef = useRef<HTMLDivElement>(null)
  const [konum, setKonum] = useState({ top: anchor.bottom + 4, left: anchor.left })

  // Ekran dışına taşmasın: sağdan ve alttan kenetle. Ölçü render'dan sonra
  // alınıyor çünkü balonun yüksekliği içeriğe (hata satırı) göre değişiyor.
  useLayoutEffect(() => {
    const yukseklik = kutuRef.current?.offsetHeight ?? 0
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - GENISLIK - 8))
    const altaSigar = anchor.bottom + 4 + yukseklik <= window.innerHeight - 8
    const top = altaSigar ? anchor.bottom + 4 : Math.max(8, anchor.top - yukseklik - 4)
    setKonum({ top, left })
  }, [anchor, hata])

  // Dışarı tıklama = vazgeç. Dinleyici efekt içinde bağlandığı için balonu
  // AÇAN tıklamanın mousedown'ı çoktan geçmiş olur; balon anında kapanmaz.
  useEffect(() => {
    const disariTikla = (event: MouseEvent) => {
      if (kutuRef.current && !kutuRef.current.contains(event.target as Node)) onClose()
    }
    const escBas = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', disariTikla)
    window.addEventListener('keydown', escBas)
    return () => {
      document.removeEventListener('mousedown', disariTikla)
      window.removeEventListener('keydown', escBas)
    }
  }, [onClose])

  async function onayla() {
    setHata(null)
    setKaydediliyor(true)
    try {
      await amendOrder(order.id, { price: Number(price), quantity: Number(quantity) })
      onClose()
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Emir düzenlenemedi')
    } finally {
      setKaydediliyor(false)
    }
  }

  const gecerli = Number(price) > 0 && Number(quantity) > 0

  return createPortal(
    <div
      ref={kutuRef}
      className="edit-pop"
      style={{ top: konum.top, left: konum.left, width: GENISLIK }}
      role="dialog"
      aria-label={`${order.id} emrini düzenle`}
    >
      <div className="edit-pop-head">
        <code>{order.id}</code>
        <span className={order.side === 'BUY' ? 'yon-al' : 'yon-sat'}>
          {order.side === 'BUY' ? '▲ Al' : '▼ Sat'}
        </span>
      </div>

      <div className="edit-pop-body">
        <label className="order-field">
          <span>Fiyat</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && gecerli && onayla()}
          />
        </label>
        <label className="order-field">
          <span>Miktar (Lot)</span>
          <input
            type="number"
            inputMode="numeric"
            step="1"
            min="0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && gecerli && onayla()}
          />
        </label>
      </div>

      {hata && <div className="edit-pop-error">{hata}</div>}

      <button
        type="button"
        className="order-submit edit-pop-ok"
        onClick={onayla}
        disabled={!gecerli || kaydediliyor}
      >
        {kaydediliyor ? '…' : 'Onayla'}
      </button>
    </div>,
    document.body,
  )
}
